import ReactEChartsCore from 'echarts-for-react/lib/core';
import { echarts, BRAND_COLOR, COLOR_PALETTE } from '../shared/echarts-config';
import { EmptyState } from '../shared/EmptyState';
import type { SearchStateData } from '../../../types/analytics.types';

type SearchHeatmapProps = {
  data: SearchStateData[];
  totalSearches: number;
  activeUserTypes: readonly string[];
};

const computeSearchCount = (
  state: SearchStateData,
  activeTypes: readonly string[],
): number => {
  if (activeTypes.length === 3) return state.totalSearches;
  let count = 0;
  if (activeTypes.includes('CARE_SEEKER')) count += state.byUserType?.CARE_SEEKER ?? 0;
  if (activeTypes.includes('FINANCIAL_ADVISOR'))
    count += state.byUserType?.FINANCIAL_ADVISOR ?? 0;
  if (activeTypes.includes('SERVICE_PROVIDER'))
    count += state.byUserType?.SERVICE_PROVIDER ?? 0;
  return count;
};

const buildMapOption = (
  data: SearchStateData[],
  activeTypes: readonly string[],
) => {
  const mapData = data.map((state) => ({
    name: state.stateName,
    value: computeSearchCount(state, activeTypes),
    stateCode: state.stateCode,
    byUserType: state.byUserType,
    topCategory: state.topCategory,
    zipcodeSearchPercent: state.zipcodeSearchPercent,
  }));

  // Exclude the no-location (UNKNOWN) bucket from the colour scale: it isn't
  // drawn on the map, so letting its (often large) count set the max washes out
  // the states that ARE drawn. The "Searches without location" header still
  // reports it separately.
  const maxValue = Math.max(
    ...mapData.filter((d) => d.stateCode !== 'UNKNOWN').map((d) => d.value),
    1,
  );

  return {
    tooltip: {
      trigger: 'item' as const,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      textStyle: { color: '#374151', fontSize: 13 },
      formatter: (params: { data?: typeof mapData[number] }) => {
        const d = params.data;
        if (!d) return '';
        return [
          `<strong>${d.name} (${d.stateCode})</strong>`,
          `Total Searches: ${d.value.toLocaleString('en-US')}`,
          `By Care Seekers: ${(d.byUserType?.CARE_SEEKER ?? 0).toLocaleString('en-US')}`,
          `By Financial Advisors: ${(d.byUserType?.FINANCIAL_ADVISOR ?? 0).toLocaleString('en-US')}`,
          `By Service Providers: ${(d.byUserType?.SERVICE_PROVIDER ?? 0).toLocaleString('en-US')}`,
          d.topCategory ? `Top Category: ${d.topCategory.categoryName}` : '',
          `Zipcode Searches: ${(d.zipcodeSearchPercent ?? 0).toFixed(1)}%`,
        ]
          .filter(Boolean)
          .join('<br/>');
      },
    },
    visualMap: {
      min: 0,
      max: maxValue,
      text: ['High', 'Low'],
      realtime: false,
      calculable: true,
      inRange: {
        color: [...COLOR_PALETTE].reverse(),
      },
      left: 'left',
      bottom: 20,
      textStyle: { color: '#6b7280', fontSize: 12 },
    },
    series: [
      {
        name: 'Searches',
        type: 'map',
        map: 'USA',
        roam: false,
        emphasis: {
          label: { show: true, fontSize: 12 },
          itemStyle: { areaColor: BRAND_COLOR, shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.3)' },
        },
        itemStyle: {
          areaColor: '#f3f4f6',
          borderColor: '#d1d5db',
          borderWidth: 0.5,
        },
        data: mapData,
      },
    ],
  };
};

export const SearchHeatmap = ({
  data,
  totalSearches,
  activeUserTypes,
}: SearchHeatmapProps) => {
  if (data.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4">
          Search Demand Heatmap
        </h3>
        <EmptyState message="No search data available for the selected date range. Try expanding the date range." />
      </div>
    );
  }

  const withLocationCount = data
    .filter((s) => s.stateCode !== 'UNKNOWN')
    .reduce((sum, s) => sum + computeSearchCount(s, activeUserTypes), 0);
  const noLocationCount = totalSearches - withLocationCount;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Search Demand Heatmap
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Total searches:{' '}
            <span className="font-semibold text-gray-900">
              {totalSearches.toLocaleString('en-US')}
            </span>
          </p>
          {noLocationCount > 0 && (
            <p className="text-sm text-gray-500">
              Searches without location:{' '}
              <span className="font-semibold text-gray-900">
                {noLocationCount.toLocaleString('en-US')}
              </span>
            </p>
          )}
        </div>
      </div>
      <ReactEChartsCore
        echarts={echarts}
        option={buildMapOption(data, activeUserTypes)}
        style={{ height: 440 }}
        notMerge
        lazyUpdate
      />
    </div>
  );
};
